import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, X } from 'lucide-react'
import { useRef } from 'react'
import { ResizeHandle } from '@/components/ResizeHandle'
import { useLayout } from '@/stores/layout'
import type { AgentSession } from '@one-deployment/api'
import { Button, SkeletonRows } from '@/components/ui'
import { Button as ShadButton } from '@/components/shadcn/button'
import { Age, ErrorState } from '@/components/status'
import { RoleGate } from '@/components/RoleGate'
import { del, enc, post } from '@/lib/api'
import { cn } from '@/lib/cn'
import { qk } from '@/lib/query'
import { ask, askText } from '@/components/ask'
import { useRunOpen } from './RunPanel'
import { useMe } from '@/features/auth/useMe'
import { SessionDock } from './SessionDock'
import { listedSessions, sessionLabel, sortSessions, useCreateSession, useProjectSessions } from './data'

export { sessionLabel, useProjectSessions, useCreateSession, listedSessions }

/** The open session, or nothing when the project page shows its tabs. */
export function useCurrentSession(project: string) {
  const { session: sid } = useParams({ strict: false }) as { session?: string }
  const { data } = useProjectSessions(project)
  return sid ? sortSessions(data?.sessions ?? []).find((s) => s.id === sid) : undefined
}

/**
 * The project's Claude sessions, down the left of the project page: people's
 * first (the oldest is "main", which can never be closed), then the robot's
 * ticket sessions. Shown until a session is open; then the breadcrumb's
 * switcher takes over, so the session gets the whole width.
 */
export function SessionSidebar({ project }: { project: string }) {
  const { session: sid } = useParams({ strict: false }) as { session?: string }
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { data, error, refetch } = useProjectSessions(project)
  const sessions = listedSessions(data?.sessions ?? [], sid)
  const me = useMe().data
  // Sessions are shared, so the list holds other people's too: "main" is
  // yours, and theirs carry their name.
  const theirs = (s: AgentSession) => !!s.ownerId && s.ownerId !== me?.id
  const main = sessions.find((s) => !s.jobId && !theirs(s) && !/-ship-/.test(s.tmuxSession))

  const create = useCreateSession(project)
  const close = useMutation({
    mutationFn: (s: AgentSession) => del(`/api/sessions/${enc(s.id)}`),
    onSuccess: (_r, s) => { if (s.id === sid) void navigate({ to: '/p/$project', params: { project } }) },
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.projectSessions(project) }),
  })
  const rename = useMutation({
    mutationFn: ({ s, label }: { s: AgentSession; label: string }) => post(`/api/sessions/${enc(s.id)}/name`, { label }),
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.projectSessions(project) }),
  })
  const askRename = (s: AgentSession) =>
    void askText({ title: 'Rename session', value: sessionLabel(s), confirm: 'Rename' }).then((label) => { if (label?.trim()) rename.mutate({ s, label: label.trim() }) })

  const { sessionsWidth, setSessionsWidth } = useLayout()
  const aside = useRef<HTMLElement>(null)

  return (
    <>
      <aside ref={aside} style={{ width: sessionsWidth }} className="flex shrink-0 flex-col">
        <div className="flex h-9 shrink-0 items-center gap-1 px-2">
          <span className="flex-1 pl-1 text-sm font-semibold">Sessions</span>
          <RoleGate action="session.manage" project={project}>
            <Button size="sm" disabled={create.isPending} onClick={() => create.mutate()}><Plus size={12} /> {create.isPending ? 'Starting…' : 'New'}</Button>
          </RoleGate>
        </div>
        {(create.error ?? close.error ?? rename.error) && <div className="p-2"><ErrorState error={create.error ?? close.error ?? rename.error} compact /></div>}
        {error && !data && <ErrorState error={error} retry={() => void refetch()} compact />}
        <div className="min-h-0 flex-1 overflow-y-auto p-1">
          {!data && !error && <SkeletonRows rows={3} />}
          {data && sessions.length === 0 && <div className="px-2 py-3 text-xs text-fg-faint">No sessions open.</div>}
          {sessions.map((s) => (
            <div key={s.id} className={cn('group flex items-center gap-1 rounded-sm pr-2', sid === s.id ? 'bg-surface-3 text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg')}>
              <Link to="/p/$project/s/$session" params={{ project, session: s.id }} className="min-w-0 flex-1 px-2 py-1.5">
                <div className="flex items-center gap-2 text-xs">
                  <span className={cn('size-1.5 shrink-0 rounded-full', s.jobId ? 'bg-info' : 'bg-ok')} title={s.jobId ? 'Robot working a ticket' : 'Person’s session'} />
                  {s.id === main?.id && <span className="rounded-xs bg-surface-3 px-1 text-2xs text-fg-subtle">main</span>}
                  <span className="truncate" onDoubleClick={(e) => { if (!s.jobId) { e.preventDefault(); askRename(s) } }}>{sessionLabel(s)}</span>
                  {theirs(s) && <span className="ml-auto shrink-0 truncate text-2xs text-fg-faint" title={`${s.ownerName ?? 'Someone'}'s session: commits made here are theirs`}>{s.ownerName ?? 'Someone'}</span>}
                </div>
                <Age from={s.startedAt} prefix="started " className="mt-0.5 block pl-3.5 text-2xs text-fg-faint" />
              </Link>
              <RoleGate action="session.manage" project={project}>
                {!s.jobId && <ShadButton variant="ghost" size="icon-xs" aria-label="Rename session" title="Rename session" className="hidden size-5 shrink-0 self-center rounded-xs text-fg-faint hover:bg-hover group-hover:inline-flex"
                  onClick={() => askRename(s)}><Pencil size={12} /></ShadButton>}
                {s.id !== main?.id && <ShadButton variant="ghost" size="icon-xs" aria-label="Close session" title="Close session" className="hidden size-5 shrink-0 self-center rounded-xs text-fg-faint hover:bg-hover hover:text-err group-hover:inline-flex"
                  onClick={() => void ask({ title: `Close ${sessionLabel(s)}?`, body: 'Whatever runs in it stops.', confirm: 'Close session', danger: true }).then((ok) => ok && close.mutate(s))}><X size={12} /></ShadButton>}
              </RoleGate>
            </div>
          ))}
        </div>
      </aside>
      <ResizeHandle label="Resize the session list" title="Drag to resize"
        onResize={(x) => setSessionsWidth(x - (aside.current?.getBoundingClientRect().left ?? 0))} />
    </>
  )
}

/** An open session: its windows. Branch and Sync are in the breadcrumbs. */
export function SessionMain({ project, session }: { project: string; session: AgentSession }) {
  const [runOpen, setRunOpen] = useRunOpen(session.id)
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {/* The dock sizes itself from its box: a plain h-full resolved to 0, a blank page. */}
      <div className="relative min-h-0 flex-1">
        <div className="absolute inset-0">
          <SessionDock session={session} project={project} run={runOpen} onRunOpen={() => setRunOpen(true)} onRunClosed={() => setRunOpen(false)} />
        </div>
      </div>
    </div>
  )
}
